import { useState, useEffect, useCallback } from 'react';
import {
  get_prop_values,
  is_object,
  is_required,
  VALUE,
  ERROR,
  getStateSchema,
} from './utilities';

/**
 * useForm hooks to handle your validation in your forms
 *
 * @param {object} stateSchema stateSchema.
 * @param {object} stateValidatorSchema stateSchemaValidation to validate your forms in react.
 * @param {function} submitFormCallback function to be execute during form submission.
 */
function useForm(
  stateSchema = {},
  stateValidatorSchema = {},
  submitFormCallback
) {
  const [state, setStateSchema] = useState(stateSchema);

  const [values, setValues] = useState(get_prop_values(state, VALUE));
  const [errors, setErrors] = useState(get_prop_values(state, ERROR));
  const [dirty, setDirty] = useState(get_prop_values(state, false));

  const [disable, setDisable] = useState(true); 
  const [isDirty, setIsDirty] = useState(false);

  // Get a local copy of stateSchema
  useEffect(() => {
    setStateSchema(stateSchema);
    setDisable(true); // Disable button in initial render.
    setInitialErrorState();
  }, []); // eslint-disable-line

  // Set a brand new field values and errors 
  // If stateSchema changes
  useEffect(() => {
    const values = get_prop_values(state, VALUE);
    setValues(values);
    setErrors(
      Object.keys(values).reduce((accu, curr) => {
        accu[curr] = validateField(curr, values[curr]);
        return accu;
      }, {})
    );
  }, [state]); // eslint-disable-line

  // For every changed in our state this will be fired
  // To be able to disable the button
  useEffect(() => {
    if (isDirty) {
      setDisable(validateErrorState());
    }
  }, [errors, isDirty]); // eslint-disable-line

  // Set a value of a specific field
  const setFieldValue = ({ name, value }) => {
    setValues(prevState => ({ ...prevState, [name]: value }));
    setDirty(prevState => ({ ...prevState, [name]: true }));
  };

  // Set an error of a specific field
  const setFieldError = ({ name, error }) =>
    setErrors(prevState => ({ ...prevState, [name]: error }));

  // Validate fields in forms
  const validateField = useCallback(
    (name, value) => {
      const validator = stateValidatorSchema;
      // Making sure that stateValidatorSchema name is same in
      // stateSchema
      if (!validator[name]) return;

      const field = validator[name];

      let error = '';
      error = is_required(value, field.required);

      if (is_object(field['validator']) && error === '') {
        const validateFieldByCallback = field['validator'];

        // Test the function callback if the value is meet the criteria
        if (!validateFieldByCallback['func'](value, values)) {
          error = validateFieldByCallback['error'];
        }
      }

      return error;
    },
    [stateValidatorSchema, values]
  );

  // Set Initial Error State
  // When hooks was first rendered...
  const setInitialErrorState = useCallback(() => {
    Object.keys(errors).map(name =>
      setFieldError({ name, error: validateField(name, values[name]) })
    );
  }, [errors, values, validateField]);

  // Used to disable submit button if there's a value in errors
  // or the required field in state has no value.
  // Wrapped in useCallback to cached the function to avoid intensive memory leaked
  // in every re-render in component
  const validateErrorState = useCallback(
    () => Object.values(errors).some(error => error),
    [errors]
  );

  // Use this callback function to safely submit the form
  // without any errors in state...
  const handleOnSubmit = useCallback(
    event => {
      event.preventDefault();

      // Making sure that there's no error in the state
      // before calling the submit callback function
      if (!validateErrorState()) {
        submitFormCallback(values);
      }
    },
    [validateErrorState, submitFormCallback, values]
  );

  // Event handler for handling changes in input.
  const handleOnChange = useCallback(
    event => {
      setIsDirty(true);
      const name = event.target.name;
      const value = event.target.value;

      const error = validateField(name, value);

      setFieldValue({ name, value });
      setFieldError({ name, error });
    },
    [validateField]
  );


  // Event handler for handling changes in input.
  const newHandleOnChange = useCallback(
    (event,target) => {
      setIsDirty(true);
      const name = target.name;
      var value="";
      if(event)
      {       
        value = event;
        const error = validateField(name, value);
        setFieldValue({ name, value });
        setFieldError({ name, error });
      }else
        setFieldValue({ name, value });         
    },
    [validateField]
  );
  

  const setData = useCallback(
    data => {
      const name = data.name;
      const value = data.value;
     
      const error = validateField(name, value);

      setFieldValue({ name, value });
      setFieldError({ name, error });

    },
    [validateField]
  );

  const setMultipleData = useCallback(
    data => {
      data.forEach(item => {
        const name = item.name;
        const value = item.value;      
        const error = validateField(name, value);
        setFieldValue({ name, value });
        setFieldError({ name, error });
      });
    },
    [validateField]
  );

  /** Reset all schema-backed fields (clears dynamic keys next sync). Preserve e.g. locale. */
  const resetFormToInitial = useCallback((preserveFields = {}) => {
    const fresh = getStateSchema();
    const merged = { ...fresh };
    Object.keys(preserveFields).forEach((key) => {
      if (merged[key]) {
        merged[key] = { ...merged[key], value: preserveFields[key] };
      }
    });
    setStateSchema(merged);
    const valueMap = get_prop_values(merged, VALUE);
    setDirty(
      Object.keys(valueMap).reduce((acc, name) => ({ ...acc, [name]: false }), {})
    );
    setIsDirty(false);
    setDisable(true);
    document.body.classList.remove('no-scroll');
  }, []);

  const handleFileChange = useCallback(
    (event, field) => {
      setIsDirty(true);
      const acceptedFileTypes = ['application/pdf'];
      //['image/jpeg', 'image/gif', 'image/png', 'application/pdf' ]
      const uploadedFile = event.target.files[0];
      let attachmentType = [];
      let error = "";
      const name = field;
      if(uploadedFile){
        attachmentType =acceptedFileTypes.filter((fileType) => fileType === uploadedFile.type);
      }    
        
      if(!attachmentType.length){ 
        error = "ERROR_INVALID_FILE_FORMAT";
      }else if(uploadedFile.size >= 1*1024*1024 ){
        error = "ERROR_MAX_FILE_SIZE";
      }else{
        error = "";        
      }
     
      const value = error === "" ? uploadedFile.name : "-";
      setFieldValue({ name, value });
      setFieldError({ name, error });
    },
    []
  ); 
  return {
    dirty,
    values,
    errors,
    disable,
    setStateSchema,
    setFieldValue,
    setFieldError,
    handleOnChange,
    newHandleOnChange,
    handleFileChange,
    handleOnSubmit,
    setData,
    setMultipleData,
    resetFormToInitial,
    validateErrorState
  };
}

export default useForm;
